/**
 * Veda Agro — Legacy sale-invoice IMPORT form (record-only). Ticket AI-4voo.
 *
 * Captures an external/ERP GST e-invoice (invoice no., IRN, Ack, e-Way bill,
 * vehicle, date, buyer, GSTINs, line items, totals, + optional PDF key) and
 * POSTs to `/veda-agro/legacy-invoices`. NO GST/accounting is recomputed — the
 * user-entered subtotal / GST / total are stored as-is.
 *
 * Rendered as a modal from LegacyInvoicesPage. Isolated new file.
 */
import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

interface AccountRow { id: string; name: string; gstin?: string | null }
interface SoRow { id: string; number?: string; formalNumber?: string | null }

interface LineItem {
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
  amount: number;
}

const GST_RATES = [0, 5, 12, 18, 28];

const emptyItem = (): LineItem => ({ description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 5, amount: 0 });

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

interface Props {
  presetSalesOrderId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export function LegacyInvoiceImportForm({ presetSalesOrderId, onClose, onSaved }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [salesOrders, setSalesOrders] = useState<SoRow[]>([]);

  const [form, setForm] = useState({
    externalNumber: '',
    irn: '',
    ackNo: '',
    ackDate: '',
    ewayBillNo: '',
    vehicleNo: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    salesOrderId: presetSalesOrderId ?? '',
    accountId: '',
    buyerGstin: '',
    sellerGstin: '',
    pdfKey: '',
  });
  const [items, setItems] = useState<LineItem[]>([emptyItem()]);

  useEffect(() => {
    api.get('/accounts?type=customer&limit=200')
      .then((r) => setAccounts(r.data.data?.accounts ?? r.data.data ?? []))
      .catch(() => {});
    api.get('/veda-agro/orders?type=so&limit=200')
      .then((r) => setSalesOrders(r.data.data?.orders ?? r.data.data ?? []))
      .catch(() => {});
  }, []);

  const setField = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (idx: number, patch: Partial<LineItem>) =>
    setItems((rows) => rows.map((row, i) => {
      if (i !== idx) return row;
      const next = { ...row, ...patch };
      // Auto-fill amount = qty*rate when the user hasn't typed a manual amount.
      if (('quantity' in patch) || ('rate' in patch)) {
        next.amount = round2(Number(next.quantity) * Number(next.rate));
      }
      return next;
    }));

  const subtotal = round2(items.reduce((s, it) => s + Number(it.amount || 0), 0));
  const totalGst = round2(items.reduce((s, it) => s + Number(it.amount || 0) * (Number(it.gstRate || 0) / 100), 0));
  const total = round2(subtotal + totalGst);

  const submit = useCallback(async () => {
    setError(null);
    if (!form.externalNumber.trim()) { setError('Invoice number is required'); return; }
    if (!form.accountId) { setError('Buyer is required'); return; }
    if (!items.length || items.some((it) => !it.description.trim())) { setError('Each line needs a description'); return; }
    try {
      setSaving(true);
      await api.post('/veda-agro/legacy-invoices', {
        externalNumber: form.externalNumber.trim(),
        irn: form.irn.trim() || null,
        ackNo: form.ackNo.trim() || null,
        ackDate: form.ackDate || null,
        ewayBillNo: form.ewayBillNo.trim() || null,
        vehicleNo: form.vehicleNo.trim() || null,
        invoiceDate: form.invoiceDate,
        salesOrderId: form.salesOrderId || null,
        accountId: form.accountId,
        buyerGstin: form.buyerGstin.trim() || null,
        sellerGstin: form.sellerGstin.trim() || null,
        pdfKey: form.pdfKey.trim() || null,
        subtotal,
        totalGst,
        total,
        items: items.map((it) => ({
          description: it.description.trim(),
          hsnSac: it.hsnSac.trim() || null,
          quantity: Number(it.quantity),
          rate: Number(it.rate),
          gstRate: Number(it.gstRate),
          amount: Number(it.amount),
        })),
      });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to import invoice');
    } finally {
      setSaving(false);
    }
  }, [form, items, subtotal, totalGst, total, onSaved]);

  const soLabel = (so: SoRow) => so.formalNumber || so.number || so.id.slice(0, 8);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-3xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-emerald-900">Import sale invoice</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          {/* Header fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Invoice no." required>
              <input value={form.externalNumber} onChange={(e) => setField('externalNumber', e.target.value)}
                placeholder="VD/DL/DI/27/153" className={inputCls} />
            </Field>
            <Field label="Invoice date" required>
              <input type="date" value={form.invoiceDate} onChange={(e) => setField('invoiceDate', e.target.value)} className={inputCls} />
            </Field>
            <Field label="IRN">
              <input value={form.irn} onChange={(e) => setField('irn', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Ack no.">
              <input value={form.ackNo} onChange={(e) => setField('ackNo', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Ack date">
              <input type="date" value={form.ackDate} onChange={(e) => setField('ackDate', e.target.value)} className={inputCls} />
            </Field>
            <Field label="e-Way bill no.">
              <input value={form.ewayBillNo} onChange={(e) => setField('ewayBillNo', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Vehicle no.">
              <input value={form.vehicleNo} onChange={(e) => setField('vehicleNo', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Buyer" required>
              <select value={form.accountId} onChange={(e) => setField('accountId', e.target.value)} className={inputCls}>
                <option value="">Select buyer…</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Link to Sales Order">
              <select value={form.salesOrderId} onChange={(e) => setField('salesOrderId', e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {salesOrders.map((so) => <option key={so.id} value={so.id}>{soLabel(so)}</option>)}
              </select>
            </Field>
            <Field label="Buyer GSTIN">
              <input value={form.buyerGstin} onChange={(e) => setField('buyerGstin', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Seller GSTIN">
              <input value={form.sellerGstin} onChange={(e) => setField('sellerGstin', e.target.value)} className={inputCls} />
            </Field>
            <Field label="PDF key (MinIO object)">
              <input value={form.pdfKey} onChange={(e) => setField('pdfKey', e.target.value)} placeholder="optional" className={inputCls} />
            </Field>
          </div>

          {/* Line items */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Line items</span>
              <button onClick={() => setItems((r) => [...r, emptyItem()])}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100">
                <Plus size={14} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })}
                    placeholder="Description" className={`${inputCls} col-span-4`} />
                  <input value={it.hsnSac} onChange={(e) => setItem(idx, { hsnSac: e.target.value })}
                    placeholder="HSN/SAC" className={`${inputCls} col-span-2`} />
                  <input type="number" value={it.quantity} onChange={(e) => setItem(idx, { quantity: Number(e.target.value) })}
                    placeholder="Qty" className={`${inputCls} col-span-1`} />
                  <input type="number" value={it.rate} onChange={(e) => setItem(idx, { rate: Number(e.target.value) })}
                    placeholder="Rate" className={`${inputCls} col-span-2`} />
                  <select value={it.gstRate} onChange={(e) => setItem(idx, { gstRate: Number(e.target.value) })}
                    className={`${inputCls} col-span-1`}>
                    {GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
                  </select>
                  <input type="number" value={it.amount} onChange={(e) => setItem(idx, { amount: Number(e.target.value) })}
                    placeholder="Amount" className={`${inputCls} col-span-1`} />
                  <button onClick={() => setItems((r) => r.filter((_, i) => i !== idx))}
                    className="col-span-1 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove line" disabled={items.length === 1}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals (entered/derived — stored as-is, no GST engine) */}
          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <Row label="Subtotal" value={subtotal} />
            <Row label="Total GST" value={totalGst} />
            <div className="flex justify-between border-t border-gray-100 pt-1 font-semibold text-emerald-900">
              <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />} Import invoice
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span><span>₹{value.toLocaleString('en-IN')}</span>
    </div>
  );
}
